import { Injectable } from '@angular/core';
import { IotService } from './iot.service';
import { BinsService } from './bins.service';
import { TaxeTypeService } from './taxe-type.service';
import { OperatorService } from './operator.service';
import { EntitiesLocationsService } from './entities-locations.service';
import { filter } from 'jszip';

@Injectable({
  providedIn: 'root'
})
export class FilterService {

  public isFilter: boolean = false;
  public lastUserFilterCount: number = 0;
  public callingComponent: string = '';
  public newArray: any;

  constructor(public iotService: IotService,
              public bin: BinsService,
              public taxeTypeService: TaxeTypeService,
              public operatorService: OperatorService,
              public entitiesLocationService: EntitiesLocationsService) { }

  // Will set the caller that use the service
  setCallingComponent(caller: string){
    this.callingComponent = caller;
  }

  // Needed for the search function to know what exactly to do whit the filter
  getCallingComponent(){
    return this.callingComponent;
  }

  /* 
    Designed to filter an array of objects based on a user-provided input (userFilter). 
    It dynamically filters the data by a specific field (filterBy), which may refer to a nested property (e.g., "bin.client_id"). 
    The function first checks if the filter string is non-empty and then applies a case-insensitive search on the specified field across all objects in the original array (originalArray). 
    The goal is to return a new filtered array containing only the objects that match the filter criteria
  */
  search(userFilter: string, array: any[], originalArray: any[], filterBy: string) {
    this.isFilter = userFilter.trim().toLowerCase() !== '';
  
    // Helper function to access nested properties like "bin.client_id"
    const getNestedValue = (obj: any, path: string): any => {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    };

    let filteredArray: any[] = [];
    array = originalArray;
    filteredArray = array.filter(a => {
      const value = getNestedValue(a, filterBy);
      return typeof value === 'string' && value.toLowerCase().includes(userFilter.toLowerCase());
    });
    
    return filteredArray;
  }
  

  /**
 * Filters a list of items based on a set of dynamic filters.
 *
 * Each filter is applied to a property path (supports nested paths using dot-notation).
 * The function handles special cases for certain fields like 'fill_level' and 'battery',
 * converting numeric/string values into categorical strings ('high', 'medium', 'low') 
 * before comparing to the filter value.
 *
 * Example usage:
 *   const filtered = filterList(devices, { 
 *     status: 'active', 
 *     fill_level: 'high' 
 *   });
 *
 * @template T - The type of items in the array.
 * @param items - The array of items to filter.
 * @param filters - An object where keys are property paths (dot-notation) and values
 *                  are the desired filter values. Null, undefined, or empty string
 *                  filter values are ignored.
 * @returns A new array containing only the items that match all provided filters.   
 */
  filterList<T>(items: T[], filters: { [path: string]: any }): T[] {
    return items.filter((item) => {
      return Object.entries(filters).every(([path, filterValue]) => {
        // Ignore null/undefined/empty array filters
        if (
          filterValue === null ||
          filterValue === undefined ||
          filterValue === ''
        ) {
          return true;
        }
        // console.log(item)
        // console.log(filters)
        // console.log(path)
        // console.log(filterValue)
        let value = this.getDeepValue(item, path);
        switch (path) {
          // 'fill_level': string percentage like "95.4%" → mapped to 'high', 'medium', 'low'
          case 'fill_level':
            value = parseFloat(value.replace('%', ''));
            if (value > 79) {
              value = 'high';
            } else if (value > 60) {
              value = 'medium';
            } else if (value > 0) {
              value = 'low';
            }
            break;
          // 'battery': numeric value → mapped to 'high', 'medium', 'low'
          case 'battery':
            if (value > 50) {
              value = 'high';
            } else if (value > 25) {
              value = 'medium';
            } else if (value > 0) {
              value = 'low';
            }
            break;
        }
        return value === filterValue;
      });
    });
  }

  /**
 * Safely retrieves a deeply nested value from an object using a dot-separated path.
 *
 * Example:
 *   getDeepValue(device, "device.bin.bin_usage")
 *   → returns the nested property value or undefined if any level is missing.
 *
 * @param obj - The source object to retrieve the value from.
 * @param path - A dot-notation string representing the nested property path.
 * @returns The value at the specified path, or undefined if it does not exist.
 */
  getDeepValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => {
      if (acc && part in acc) return acc[part];
      return undefined;
    }, obj);
  }


}
